import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { PieChart, Pie, ResponsiveContainer, Cell, Tooltip } from "recharts";
export interface Department {
  name: string;
  participants: number;
}
export interface NetworkStatsProps {
  totalParticipants: number;
  activeContributors: number | string;
  knowledgeBases: number;
  totalSolutions: number;
  departments: Department[];
  channelName: string;
  solutionsLabel?: string;
  disableMarketplaceLink?: boolean;
}
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];
const getMarketplaceName = (channelName: string) => {
  const names: Record<string, string> = {
    'curt': 'CURT Marketplace',
    'chevron': 'Chevron Marketplace',
    'ispai': 'Marketplace',
    'gsa': 'Government Marketplace'
  };
  return names[channelName] || `${channelName.toUpperCase()} Marketplace`;
};
const getMarketplaceRoute = (channelName: string) => {
  const routes: Record<string, string> = {
    'chevron': '/chevron-solutions',
    'gsa': '/gsa-solutions',
    'ispai': '/ispai-marketplace'
  };
  return routes[channelName] || `/channel-solutions?channel=${channelName}`;
};
const KnowledgeNetworkStats = ({
  totalParticipants,
  activeContributors,
  knowledgeBases,
  totalSolutions,
  departments,
  channelName,
  solutionsLabel = "Solutions",
  disableMarketplaceLink = false
}: NetworkStatsProps) => {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Card className="p-6 bg-white shadow-sm">
        <h3 className="text-lg font-semibold mb-4">Network Participants</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <p className="text-gray-600 mb-1">mySPAI Profiles</p>
            <p className="text-3xl font-bold text-primary">{totalParticipants.toLocaleString()}</p>
          </div>
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <p className="text-gray-600 mb-1">{solutionsLabel}</p>
            <p className="text-3xl font-bold text-primary">{typeof activeContributors === 'string' ? activeContributors : activeContributors.toLocaleString()}</p>
          </div>
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <p className="text-gray-600 mb-1">Knowledge Bases</p>
            <p className="text-3xl font-bold text-primary">{knowledgeBases.toLocaleString()}</p>
          </div>
          {disableMarketplaceLink ? (
            <div className="text-center p-4 bg-green-100 rounded-lg flex flex-col items-center justify-center">
              <p className="text-gray-700 mb-1">{getMarketplaceName(channelName)}</p>
              <p className="text-3xl font-bold text-green-700">{totalSolutions.toLocaleString()}</p>
            </div>
          ) : (
            <Link to="/ispai-marketplace" className="text-center p-4 bg-green-100 rounded-lg cursor-pointer hover:bg-green-200 transition-colors flex flex-col items-center justify-center group">
              <p className="text-gray-700 mb-1 group-hover:font-medium transition-all">Marketplace</p>
              <p className="text-3xl font-bold text-green-700">{totalSolutions.toLocaleString()}</p>
            </Link>
          )}
        </div>
      </Card>
      
      <Card className="p-6 bg-white shadow-sm flex flex-col">
        <h3 className="text-lg font-semibold mb-4">Participant Distribution</h3>
        <div className="flex flex-grow">
          <div className="w-1/2 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={departments} cx="50%" cy="50%" labelLine={false} outerRadius={80} fill="#8884d8" dataKey="participants">
                  {departments.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={value => [`${value} participants`, '']} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="w-1/2">
            <ul className="space-y-2">
              {departments.map((dept, index) => <li key={dept.name} className="flex items-center text-sm">
                  <span className="w-3 h-3 rounded-full mr-2" style={{
                backgroundColor: COLORS[index % COLORS.length]
              }}></span>
                  <span className="flex-1">{dept.name}</span>
                  <span className="font-medium">{dept.participants}</span>
                </li>)}
            </ul>
          </div>
        </div>
      </Card>
    </div>;
};
export default KnowledgeNetworkStats;